import { Model } from '@polleverywhere/backbone'

/**
 * These are properties computed from other values in the state
 *
 * The `compute` function returns the current value and should be
 * 'pure' to not cause any side effects
 */
const COMPUTED = {
  canRegister: {
    listenTo () {
      return [
        [this.presenter.registrationInfo, 'change:participant_self_registration']
      ]
    },
    compute () {
      return !!this.presenter.registrationInfo.get('participant_self_registration')
    }
  },
  needsToPay: {
    listenTo () {
      return [
        [this.presenter.registrationInfo, 'change:participation_cost']
      ]
    },
    compute () {
      return this.presenter.registrationInfo.needsToPay()
    }
  },
  needsLogin: {
    listenTo () {
      return [
        [this.user, 'change:id']
      ]
    },
    compute () {
      return !this.user.isLoggedIn()
    }
  },
  isRegistered: {
    listenTo () {
      return [
        [this.presenter.registrationInfo, 'change:participant_self_registration'],
        [this, 'change:needsToPay']
      ]
    },
    compute () {
      return this.presenter.registrationInfo.isExistingParticipant() && !this.needsToPay
    }
  },
  accountIdentifier: {
    listenTo () {
      return [
        [this.presenter.registrationInfo, 'change:organization'],
        [this.presenter, 'change:name']
      ]
    },
    compute () {
      const fullName = this.presenter.get('name')
      const org = this.presenter.registrationInfo.get('organization')
      const accountName = org ? `${org}'s account` : 'their account'

      return `${fullName}, and additional users on ${accountName}, `
    }
  },
  initiallyRegistered: {
    listenTo () {
      return []
    },
    compute () {
      return this.presenter.registrationInfo.isExistingParticipant()
    }
  }
}

/**
 * These are will run when any `listenTo` property changes and on init if
 * `initial` is true
 *
 * The `run` function does any work needed, be it fetching or logging data
 */
const AUTORUN = {
  resetPage: {
    listenTo () {
      return [
        [this, 'change:needsLogin change:canRegister']
      ]
    },
    run () {
      const signupOrLogin = this.page === 'login' || this.page === 'signup'
      if (!this.canRegister && this.page !== 'blocked') {
        this.goto('blocked')
      } else if (this.needsLogin && !signupOrLogin) {
        this.goto('login')
      } else if (signupOrLogin || this.page === 'blocked') {
        this.goto(this.isRegistered ? 'registered' : 'intro')
      } else if (this.isRegistered && this.page === 'intro') {
        this.goto('registered')
      }
    },
    initial: true
  }
}

/**
 * Common state for register pages (/:presenter/register)
 *
 * So that we can keep everything in sync without passing events around everywhere.
 *
 * Based on MobX conventions, adapted to work with Backbone (waiting on MobX 6 and
 * larger refactor). Getters should be used to access properties, and `set` should
 * never be called from outside this class, use 'actions' (methods) to change state
 */
export default class RegisterFlowState extends Model {
  get defaults () {
    return {
      user: undefined,
      participant: undefined,
      presenter: undefined,
      session: undefined,
      page: 'intro',
      canRegister: true,
      needsToPay: false,
      needsLogin: false,
      isRegistered: false,
      accountIdentifier: '',
      initiallyRegistered: false
    }
  }

  get user () {
    return this.get('user')
  }

  get participant () {
    return this.get('participant')
  }

  get presenter () {
    return this.get('presenter')
  }

  get session () {
    return this.get('session')
  }

  get page () {
    return this.get('page')
  }

  get canRegister () {
    return this.get('canRegister')
  }

  get needsToPay () {
    return this.get('needsToPay')
  }

  get needsLogin () {
    return this.get('needsLogin')
  }

  get isRegistered () {
    return this.get('isRegistered')
  }

  get accountIdentifier () {
    return this.get('accountIdentifier')
  }

  get initiallyRegistered () {
    return this.get('initiallyRegistered')
  }

  initialize () {
    if (!this.user) { throw new Error('requires user') }
    if (!this.presenter) { throw new Error('requires presenter') }
    if (!this.session) { throw new Error('requires session') }

    for (const [attr, def] of Object.entries(COMPUTED)) {
      def.listenTo.call(this).forEach(([obj, listener]) => {
        this.listenTo(obj, listener, () => this.set(attr, def.compute.call(this)))
      })
      this.set(attr, def.compute.call(this))
    }

    for (const [, def] of Object.entries(AUTORUN)) {
      def.listenTo.call(this).forEach(([obj, listener]) => {
        this.listenTo(obj, listener, () => def.run.call(this))
      })
      if (def.initial) {
        def.run.call(this)
      }
    }
  }

  goto (page) {
    this.set('page', page)
  }
}
